import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Undo2, Trash2, Loader2, Check, Pencil, ArrowUpRight, Type } from "lucide-react";

const COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#111827", "#ffffff"];
const SIZES = [3, 6, 10, 16];
const TOOLS = [
  { key: "pen", label: "Penna", Icon: Pencil },
  { key: "arrow", label: "Freccia", Icon: ArrowUpRight },
  { key: "text", label: "Testo", Icon: Type },
];

export default function PhotoAnnotator({ fileId, fileName, onClose, onSaved }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const itemsRef = useRef([]); // {type, color, size, ...}
  const drawingRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [color, setColor] = useState("#ef4444");
  const [size, setSize] = useState(6);
  const [tool, setTool] = useState("pen");
  const [textDraft, setTextDraft] = useState(null); // {dispX,dispY,x,y,value,fontDisp}
  const [, force] = useState(0);

  useEffect(() => {
    let url;
    (async () => {
      try {
        const { data } = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
        url = URL.createObjectURL(data);
        const img = new Image();
        img.onload = () => {
          imgRef.current = img;
          const canvas = canvasRef.current;
          if (canvas) {
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            redraw();
          }
          setLoading(false);
        };
        img.onerror = () => { toast.error("Impossibile caricare l'immagine"); onClose?.(); };
        img.src = url;
      } catch {
        toast.error("Impossibile caricare l'immagine");
        onClose?.();
      }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [fileId]);

  const fontPx = (sz) => sz * 4;

  const drawArrow = (ctx, it) => {
    const { x1, y1, x2, y2, size: s, color: c } = it;
    ctx.strokeStyle = c; ctx.fillStyle = c; ctx.lineWidth = s;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    const ang = Math.atan2(y2 - y1, x2 - x1);
    const head = Math.max(12, s * 3.5);
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - head * Math.cos(ang - Math.PI / 6), y2 - head * Math.sin(ang - Math.PI / 6));
    ctx.lineTo(x2 - head * Math.cos(ang + Math.PI / 6), y2 - head * Math.sin(ang + Math.PI / 6));
    ctx.closePath(); ctx.fill();
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
    ctx.lineJoin = "round"; ctx.lineCap = "round";
    for (const it of itemsRef.current) {
      if (it.type === "pen") {
        ctx.strokeStyle = it.color; ctx.lineWidth = it.size;
        ctx.beginPath();
        it.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.stroke();
      } else if (it.type === "arrow") {
        drawArrow(ctx, it);
      } else if (it.type === "text") {
        const fp = fontPx(it.size);
        ctx.font = `bold ${fp}px sans-serif`;
        ctx.textBaseline = "top";
        ctx.lineWidth = Math.max(2, fp / 8);
        ctx.strokeStyle = "rgba(255,255,255,0.85)";
        ctx.strokeText(it.text, it.x, it.y);
        ctx.fillStyle = it.color;
        ctx.fillText(it.text, it.x, it.y);
      }
    }
  };

  const toCanvas = (e) => {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dispX = clientX - r.left, dispY = clientY - r.top;
    return {
      dispX, dispY,
      ratio: canvas.width / r.width,
      x: dispX * (canvas.width / r.width),
      y: dispY * (canvas.height / r.height),
    };
  };

  const start = (e) => {
    if (loading) return;
    e.preventDefault();
    const p = toCanvas(e);
    if (tool === "text") {
      setTextDraft({ dispX: p.dispX, dispY: p.dispY, x: p.x, y: p.y, value: "", fontDisp: fontPx(size) / p.ratio });
      return;
    }
    drawingRef.current = true;
    if (tool === "pen") {
      itemsRef.current.push({ type: "pen", color, size, points: [{ x: p.x, y: p.y }] });
    } else if (tool === "arrow") {
      itemsRef.current.push({ type: "arrow", color, size, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
    }
    redraw();
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = toCanvas(e);
    const it = itemsRef.current[itemsRef.current.length - 1];
    if (it.type === "pen") it.points.push({ x: p.x, y: p.y });
    else if (it.type === "arrow") { it.x2 = p.x; it.y2 = p.y; }
    redraw();
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const it = itemsRef.current[itemsRef.current.length - 1];
    // rimuovi frecce/tratti nulli
    if (it && it.type === "arrow" && it.x1 === it.x2 && it.y1 === it.y2) itemsRef.current.pop();
    redraw();
    force((n) => n + 1);
  };

  const commitText = () => {
    const d = textDraft;
    if (d && d.value.trim()) {
      itemsRef.current.push({ type: "text", color, size, x: d.x, y: d.y, text: d.value });
    }
    setTextDraft(null);
    redraw();
    force((n) => n + 1);
  };

  const undo = () => { itemsRef.current.pop(); redraw(); force((n) => n + 1); };
  const clearAll = () => { itemsRef.current = []; setTextDraft(null); redraw(); force((n) => n + 1); };

  const save = async () => {
    if (textDraft) commitText();
    setSaving(true);
    try {
      const blob = await new Promise((r) => canvasRef.current.toBlob(r, "image/jpeg", 0.85));
      const fd = new FormData();
      fd.append("file", new File([blob], fileName || "foto.jpg", { type: "image/jpeg" }));
      const { data } = await api.put(`/files/${fileId}/content`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto annotata salvata");
      onSaved?.(data);
      onClose?.();
    } catch {
      toast.error("Errore durante il salvataggio della foto");
    } finally {
      setSaving(false);
    }
  };

  const hasItems = itemsRef.current.length > 0;

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-3" data-testid="photo-annotator" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2 text-sm font-semibold"><Pencil className="w-4 h-4" style={{ color: "var(--primary)" }} /> Disegna sulla foto</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5" data-testid="annot-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-1">
            {TOOLS.map(({ key, label, Icon }) => (
              <button key={key} onClick={() => setTool(key)} data-testid={`annot-tool-${key}`} title={label}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold border transition-colors"
                style={{ borderColor: tool === key ? "var(--primary)" : "var(--border)", background: tool === key ? "var(--surface-muted)" : "#fff", color: tool === key ? "var(--primary)" : "var(--text-muted)" }}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} data-testid={`annot-color-${c}`} title="Colore"
                className="w-6 h-6 rounded-full border-2 transition-transform"
                style={{ background: c, borderColor: color === c ? "var(--primary)" : "rgba(0,0,0,0.15)", transform: color === c ? "scale(1.15)" : "none" }} />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {SIZES.map((sz) => (
              <button key={sz} onClick={() => setSize(sz)} data-testid={`annot-size-${sz}`} title="Spessore / dimensione testo"
                className="rounded-full border flex items-center justify-center"
                style={{ width: 28, height: 28, borderColor: size === sz ? "var(--primary)" : "var(--border)", background: size === sz ? "var(--surface-muted)" : "#fff" }}>
                <span className="rounded-full bg-black inline-block" style={{ width: sz, height: sz }} />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={undo} disabled={!hasItems} className="btn-secondary text-xs disabled:opacity-40" data-testid="annot-undo"><Undo2 className="w-3.5 h-3.5" /> Annulla</button>
            <button onClick={clearAll} disabled={!hasItems} className="btn-secondary text-xs disabled:opacity-40" data-testid="annot-clear"><Trash2 className="w-3.5 h-3.5" /> Cancella</button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-[var(--surface-muted)] flex items-center justify-center p-3 relative" style={{ minHeight: 200 }}>
          {loading && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>}
          <div className="relative inline-block" style={{ lineHeight: 0 }}>
            <canvas
              ref={canvasRef}
              className="max-w-full touch-none select-none"
              style={{ maxHeight: "58vh", cursor: tool === "text" ? "text" : "crosshair", background: "#fff", visibility: loading ? "hidden" : "visible" }}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="annot-canvas"
            />
            {textDraft && (
              <input
                autoFocus
                data-testid="annot-text-input"
                value={textDraft.value}
                onChange={(e) => setTextDraft({ ...textDraft, value: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") commitText(); if (e.key === "Escape") setTextDraft(null); }}
                onBlur={commitText}
                placeholder="Scrivi e premi Invio"
                className="absolute outline-none border bg-white/90 px-1 rounded"
                style={{ left: textDraft.dispX, top: textDraft.dispY, color, fontWeight: "bold", fontSize: Math.max(12, textDraft.fontDisp), borderColor: "var(--primary)" }}
              />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="btn-secondary text-sm" data-testid="annot-cancel">Chiudi</button>
          <button onClick={save} disabled={saving || loading} className="btn-primary text-sm disabled:opacity-50" data-testid="annot-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Salva foto
          </button>
        </div>
      </div>
    </div>
  );
}
